import {Exclude, Expose, instanceToPlain, plainToInstance, Transform, Type} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity} from "../entity";
import {AddressCodec, AddressEntity} from "../address";
import {RrcDistrictCode, toRrcDistrictCodeCode} from "./rrc-district-code";
import {getDatabaseCodeTransformer} from "../transfomers/transformers";
import {RrcCompanyRole, toRrcCompanyRole} from "./rrc-company-role";
import {RrcAutoSignStrategy, toRrcAutoSignStrategy} from "./rrc-auto-sign-strategy";

export const RrcCompanyProfileCodec = t.intersection([
  t.type({
    id: t.string,
    profileKey: t.string,
    effectiveStartDttm: t.number,
    version: t.number,
    status: t.string,
    companyId: t.string,
    rrcDistrict: t.string,
    role: t.string,
    commercial: t.boolean,
    snapshotJson: t.string,
    snapshotHash: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    supersedesId: t.string,
    supersededById: t.string,
    effectiveEndDttm: t.number,
    orgName: t.string,
    p5Number: t.string,
    permitNumber: t.string,
    contactName: t.string,
    contactPhone: t.string,
    contactEmail: t.string,
    address: AddressCodec,
    autoEp9SignerId: t.string,
    autoEp9SignerStrategy: t.string,
    autoEp9SignerName: t.string,
    autoEp10SignerId: t.string,
    autoEp10SignerStrategy: t.string,
    autoEp10SignerName: t.string,
    createdById: t.string,
    lastModifiedById: t.string,
    createdByName: t.string,
    lastModifiedByName: t.string,
    regionIds: t.array(t.string),
    companyName: t.string,
  }),
]);

export type RrcCompanyProfile = t.TypeOf<typeof RrcCompanyProfileCodec>;

@Exclude()
export class RrcCompanyProfileEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  profileKey!: string;

  @Expose()
  effectiveStartDttm!: number;

  @Expose()
  effectiveEndDttm?: number;

  @Expose()
  version!: number;

  @Expose()
  status!: string;

  @Expose()
  supersedesId?: string;

  @Expose()
  supersededById?: string;

  @Expose()
  companyId!: string;

  @Expose()
  companyName?: string;

  @Transform(getDatabaseCodeTransformer(toRrcDistrictCodeCode))
  @Expose()
  rrcDistrict!: RrcDistrictCode;

  @Transform(getDatabaseCodeTransformer(toRrcCompanyRole))
  @Expose()
  role!: RrcCompanyRole;

  @Expose()
  orgName?: string;

  @Expose()
  p5Number?: string;

  @Expose()
  permitNumber?: string;

  @Expose()
  commercial = false

  @Expose()
  contactName?: string;

  @Expose()
  contactPhone?: string;

  @Expose()
  contactEmail?: string;

  @Expose()
  @Type(() => AddressEntity)
  address?: AddressEntity

  @Expose()
  autoEp9SignerId?: string;

  @Expose()
  autoEp9SignerName?: string;

  @Transform(getDatabaseCodeTransformer(toRrcAutoSignStrategy))
  @Expose()
  autoEp9SignerStrategy?: RrcAutoSignStrategy

  @Expose()
  autoEp10SignerId?: string;

  @Expose()
  autoEp10SignerName?: string;

  @Transform(getDatabaseCodeTransformer(toRrcAutoSignStrategy))
  @Expose()
  autoEp10SignerStrategy?: RrcAutoSignStrategy

  @Expose()
  snapshotJson!: string;

  @Expose()
  snapshotHash!: string;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  @Expose()
  regionIds?: string[];

  @Expose()
  createdByName?: string;

  @Expose()
  lastModifiedByName?: string;

  readonly entityType = 'RRCCOMPANYPROFILE';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for RrcCompanyProfileEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: RrcCompanyProfile): RrcCompanyProfileEntity {
    return plainToInstance(RrcCompanyProfileEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<RrcCompanyProfile> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(RrcCompanyProfileCodec, object);
  }

  asTypeValue(): RrcCompanyProfile {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(RrcCompanyProfileCodec, object);
  }
}

export interface RrcCompanyProfileCreateDTO {
  profileKey?: string;
  effectiveStartDttm: number;
  effectiveEndDttm?: number
  companyId: string;
  rrcDistrict: string;
  role: string;
  orgName?: string;
  p5Number?: string;
  permitNumber?: string;
  commercial?: boolean;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: AddressEntity;
  autoEp9SignerId?: string;
  autoEp9SignerStrategy?: string;
  autoEp10SignerId?: string;
  autoEp10SignerStrategy?: string;
}

export interface RrcCompanyProfileUpdateDTO {
  id: string
  effectiveStartDttm: number;
  effectiveEndDttm?: number
  companyId: string;
  rrcDistrict: string;
  role: string;
  orgName?: string;
  p5Number?: string;
  permitNumber?: string;
  commercial?: boolean;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: AddressEntity;
  autoEp9SignerId?: string;
  autoEp9SignerStrategy?: string;
  autoEp10SignerId?: string;
  autoEp10SignerStrategy?: string;
}
